const EXAM_TYPE = {
  DEFAULT: "DEFAULT",
  H24: "H24",
  ECHANTILLON: "ECHANTILLON",
  SEROLOGIE: "SEROLOGIE",
  SANG: "SANG",
  URINE: "URINE",
  PCR: "PCR",
  SELLE: "SELLE",
  AUTRE: "AUTRE",
};

const EXAM_TYPE_COLOR_BG = {
  DEFAULT: "bg-gray-100",
  URINE: "bg-primary-100",
  ECHANTILLON: "bg-purple-100",
  H24: "bg-green-100",
  SEROLOGIE: "bg-yellow-100",
  SANG: "bg-red-100",
  PCR: "bg-pink-100",
  SELLE: "bg-violet-100",
  AUTRE: "bg-orange-100",
};

const EXAM_TYPE_COLOR_TEXT = {
  DEFAULT: "text-gray-400",
  URINE: "text-primary-400",
  ECHANTILLON: "text-purple-400",
  H24: "text-green-400",
  SEROLOGIE: "text-yellow-400",
  SANG: "text-red-400",
  PCR: "text-pink-400",
  SELLE: "text-violet-400",
  AUTRE: "text-orange-400",
};

const EXAM_TYPE_COLOR_BORDER = {
  DEFAULT: "border-gray-200",
  URINE: "border-primary-200",
  ECHANTILLON: "border-purple-200",
  H24: "border-green-200",
  SEROLOGIE: "border-yellow-200",
  SANG: "border-red-200",
  PCR: "border-pink-200",
  SELLE: "border-violet-200",
  AUTRE: "border-orange-200",
};

const EXAM_TYPE_LABEL = {
  DEFAULT: "Défaut",
  URINE: "Urine",
  ECHANTILLON: "Échantillon",
  H24: "Urines 24h",
  SEROLOGIE: "Sérologie",
  SANG: "Sang",
  PCR: "PCR",
  SELLE: "Selles",
  AUTRE: "Autre",
};

const REVIEW_STATUS = {
  PENDING: "PENDING",
  DONE: "DONE",
};

const CONFIG_STATUS = {
  CREATED: "CREATED",
  UPDATED: "UPDATED",
  VERIFIED: "VERIFIED",
};

const CONFIG_STATUS_LABEL = {
  [CONFIG_STATUS.CREATED]: "Créé",
  [CONFIG_STATUS.UPDATED]: "Mis à jour",
  [CONFIG_STATUS.VERIFIED]: "Vérifié",
};

const SYNC_SOURCE = {
  KALISIL: "KALISIL",
  UBILAB: "UBILAB",
};

const SYNC_SOURCE_LABEL = {
  [SYNC_SOURCE.KALISIL]: "KaliSIL",
  [SYNC_SOURCE.UBILAB]: "Ubilab",
};

const SEXE_LABEL = {
  M: "Homme",
  F: "Femme",
};

const USER_ROLE_LABEL = {
  user: "Utilisateur",
  admin: "Admin",
  superadmin: "Super admin",
};

const DAY_LABEL = {
  MONDAY: "Lundi",
  TUESDAY: "Mardi",
  WEDNESDAY: "Mercredi",
  THURSDAY: "Jeudi",
  FRIDAY: "Vendredi",
  SATURDAY: "Samedi",
  SUNDAY: "Dimanche",
};

const ADMISSION_STATUS = {
  DRAFT: "DRAFT",
  TO_VALIDATE: "TO_VALIDATE",
  ON_HOLD: "ON_HOLD",
  VALIDATED: "VALIDATED",
  FINALIZING: "FINALIZING",
  ERROR: "ERROR",
  QUEUED: "QUEUED",
  RECORDED: "RECORDED",
  ARCHIVED: "ARCHIVED",
};

const TERMINAL_ADMISSION_STATUSES = [ADMISSION_STATUS.QUEUED, ADMISSION_STATUS.RECORDED, ADMISSION_STATUS.ARCHIVED];

const ADMISSION_STATUS_REASON = {
  REJECTED: "REJECTED",
  ABANDONED: "ABANDONED",
  MERGED: "MERGED",
  PARTIAL: "PARTIAL",
};

const STATUS_REASON_LABELS = {
  [ADMISSION_STATUS_REASON.REJECTED]: "Refusé",
  [ADMISSION_STATUS_REASON.ABANDONED]: "Abandonné",
  [ADMISSION_STATUS_REASON.MERGED]: "Fusionné",
  [ADMISSION_STATUS_REASON.PARTIAL]: "Partiel",
};

const STATUS_CONFIG = {
  [ADMISSION_STATUS.DRAFT]: { label: "Brouillon" },
  [ADMISSION_STATUS.TO_VALIDATE]: { label: "À valider" },
  [ADMISSION_STATUS.ON_HOLD]: { label: "En attente" },
  [ADMISSION_STATUS.VALIDATED]: { label: "Validé" },
  [ADMISSION_STATUS.FINALIZING]: { label: "En finalisation" },
  [ADMISSION_STATUS.ERROR]: { label: "En erreur" },
  [ADMISSION_STATUS.QUEUED]: { label: "En cours d'enregistrement" },
  [ADMISSION_STATUS.RECORDED]: { label: "Enregistré" },
  [ADMISSION_STATUS.ARCHIVED]: { label: "Archivé" },
};

const FORCE_STATUS_TARGETS = [ADMISSION_STATUS.TO_VALIDATE, ADMISSION_STATUS.RECORDED, ADMISSION_STATUS.ARCHIVED];

const getAllowedForceStatusTargets = (fromStatus) => {
  if (fromStatus === ADMISSION_STATUS.TO_VALIDATE || fromStatus === ADMISSION_STATUS.ON_HOLD || fromStatus === ADMISSION_STATUS.VALIDATED) {
    return [ADMISSION_STATUS.RECORDED, ADMISSION_STATUS.ARCHIVED];
  }
  if (fromStatus === ADMISSION_STATUS.FINALIZING || fromStatus === ADMISSION_STATUS.ERROR || fromStatus === ADMISSION_STATUS.QUEUED) {
    return [ADMISSION_STATUS.TO_VALIDATE, ADMISSION_STATUS.RECORDED, ADMISSION_STATUS.ARCHIVED];
  }
  if (fromStatus === ADMISSION_STATUS.RECORDED) {
    return [ADMISSION_STATUS.TO_VALIDATE, ADMISSION_STATUS.ARCHIVED];
  }
  if (fromStatus === ADMISSION_STATUS.ARCHIVED) {
    return [ADMISSION_STATUS.TO_VALIDATE];
  }
  return [];
};

const FIELD_STATE = {
  DISABLE: "DISABLE",
  OPTIONAL: "OPTIONAL",
  REQUIRE_FORCEABLE: "REQUIRE_FORCEABLE",
  REQUIRE: "REQUIRE",
};

const resolveActiveFields = (activeFields, laboratory) => {
  const overrides = laboratory?.activeFieldsOverrides || {};
  return Object.fromEntries(Object.entries(activeFields || {}).map(([category, fields]) => [category, { ...fields, ...overrides[category] }]));
};

const isRequiredFieldState = (fieldState) => fieldState === FIELD_STATE.REQUIRE || fieldState === FIELD_STATE.REQUIRE_FORCEABLE;

const EMAIL_TYPE = {
  CONFIRMATION: "CONFIRMATION",
  MORE_QUESTIONS: "MORE_QUESTIONS",
  OTP: "OTP",
  REJECTION: "REJECTION",
  CUSTOM: "CUSTOM",
  HPRIM: "HPRIM",
};

const EMAIL_TYPE_LABELS = {
  [EMAIL_TYPE.CONFIRMATION]: "Confirmation",
  [EMAIL_TYPE.MORE_QUESTIONS]: "Informations complémentaires",
  [EMAIL_TYPE.OTP]: "Code de connexion",
  [EMAIL_TYPE.REJECTION]: "Refus",
  [EMAIL_TYPE.CUSTOM]: "Message personnalisé",
  [EMAIL_TYPE.HPRIM]: "HPRIM",
};

const EMAIL_TYPE_COLORS = {
  [EMAIL_TYPE.CONFIRMATION]: { bg: "bg-emerald-100", text: "text-emerald-700", border: "border-emerald-200", icon: "text-emerald-500" },
  [EMAIL_TYPE.MORE_QUESTIONS]: { bg: "bg-blue-100", text: "text-blue-700", border: "border-blue-200", icon: "text-blue-500" },
  [EMAIL_TYPE.OTP]: { bg: "bg-amber-100", text: "text-amber-700", border: "border-amber-200", icon: "text-amber-500" },
  [EMAIL_TYPE.REJECTION]: { bg: "bg-red-100", text: "text-red-700", border: "border-red-200", icon: "text-red-500" },
  [EMAIL_TYPE.CUSTOM]: { bg: "bg-purple-100", text: "text-purple-700", border: "border-purple-200", icon: "text-purple-500" },
  [EMAIL_TYPE.HPRIM]: { bg: "bg-gray-100", text: "text-gray-700", border: "border-gray-200", icon: "text-gray-500" },
};

// Ordre des clés = ordre de prélèvement sanguin (hémocultures → citrate → sec → héparine → EDTA → fluorure), puis autres par ordre alphabétique
const TUBE_TYPE = {
  FLACON_AEROBIE: "FLACON_AEROBIE",
  NEUTRE_TRANSPARENT: "NEUTRE_TRANSPARENT",
  CITRATE: "CITRATE",
  PLASMA_CITR: "PLASMA_CITR",
  SEC_GEL: "SEC_GEL",
  SEC_SANS_GEL: "SEC_SANS_GEL",
  HEPARINE_AVEC_GEL: "HEPARINE_AVEC_GEL",
  HEPARINE_SANS_GEL: "HEPARINE_SANS_GEL",
  EDTA: "EDTA",
  EDTA_APROTININE: "EDTA_APROTININE",
  FLUORURE: "FLUORURE",
  ACD_B: "ACD_B",
  BOITE_PETRI: "BOITE_PETRI",
  CYTOBROSSE_ANALE: "CYTOBROSSE_ANALE",
  CYTOBROSSE_COL_UTERUS: "CYTOBROSSE_COL_UTERUS",
  ECOUVILLON_FLOQSWAB: "ECOUVILLON_FLOQSWAB",
  ECOUVILLON_TUBE: "ECOUVILLON_TUBE",
  ESWAB: "ESWAB",
  FECALSWAB: "FECALSWAB",
  FLACON_URINE_12H: "FLACON_URINE_12H",
  FLACON_URINE_24H: "FLACON_URINE_24H",
  M4RT: "M4RT",
  POT: "POT",
  POT_BIOPSIE: "POT_BIOPSIE",
  POT_CALCUL_URINAIRE: "POT_CALCUL_URINAIRE",
  POT_EXPECTORATION: "POT_EXPECTORATION",
  POT_SELLES: "POT_SELLES",
  POT_STERILE: "POT_STERILE",
  POT_TUBE_LCR: "POT_TUBE_LCR",
  POT_TUBE_LIQUIDE: "POT_TUBE_LIQUIDE",
  POT_URINE_TUBE_BEIGE: "POT_URINE_TUBE_BEIGE",
  POT_URINE_TUBE_VERT: "POT_URINE_TUBE_VERT",
  POT_URINES: "POT_URINES",
  POT_URINES_ADDIS: "POT_URINES_ADDIS",
  POT_URINES_TUBE: "POT_URINES_TUBE",
  SANG_TOTAL: "SANG_TOTAL",
  SARSTEDT: "SARSTEDT",
  SCOTCH: "SCOTCH",
  SELLES: "SELLES",
  SERINGUE: "SERINGUE",
  SERINGUE_HEPARINEE: "SERINGUE_HEPARINEE",
  SERUM: "SERUM",
  SOUCHE: "SOUCHE",
  STRECK: "STRECK",
  TISSU_FFPE: "TISSU_FFPE",
  TUBE_AIR_EXPIRE: "TUBE_AIR_EXPIRE",
  TUBE_SANG_SELLES: "TUBE_SANG_SELLES",
  URINE: "URINE",
  URINE_UAP: "URINE_UAP",
  URINES_5H: "URINES_5H",
};

const TUBE_TYPE_LABELS = {
  [TUBE_TYPE.ACD_B]: "ACD-B",
  [TUBE_TYPE.BOITE_PETRI]: "Boite Petri",
  [TUBE_TYPE.CITRATE]: "Citrate",
  [TUBE_TYPE.CYTOBROSSE_ANALE]: "Cytobrosse (anale)",
  [TUBE_TYPE.CYTOBROSSE_COL_UTERUS]: "Cytobrosse (col utérus)",
  [TUBE_TYPE.ECOUVILLON_FLOQSWAB]: "Ecouvillon floqswab (HPV)",
  [TUBE_TYPE.ECOUVILLON_TUBE]: "Ecouvillon + tube (covid/grippe/vrs)",
  [TUBE_TYPE.EDTA]: "EDTA",
  [TUBE_TYPE.EDTA_APROTININE]: "EDTA aprotinine",
  [TUBE_TYPE.ESWAB]: "Eswab (microbio)",
  [TUBE_TYPE.FECALSWAB]: "Fecalswab (selles)",
  [TUBE_TYPE.FLACON_AEROBIE]: "Hémocultures",
  [TUBE_TYPE.FLACON_URINE_12H]: "Flacon (urines de 12h)",
  [TUBE_TYPE.FLACON_URINE_24H]: "Flacon (urines de 24h)",
  [TUBE_TYPE.FLUORURE]: "Fluorure",
  [TUBE_TYPE.HEPARINE_AVEC_GEL]: "Héparine",
  [TUBE_TYPE.HEPARINE_SANS_GEL]: "Héparine (sans gel)",
  [TUBE_TYPE.M4RT]: "Tube M4RT",
  [TUBE_TYPE.NEUTRE_TRANSPARENT]: "Neutre transparent",
  [TUBE_TYPE.PLASMA_CITR]: "Plasma citrate",
  [TUBE_TYPE.POT]: "Pot",
  [TUBE_TYPE.POT_BIOPSIE]: "Pot (biopsie)",
  [TUBE_TYPE.POT_CALCUL_URINAIRE]: "Pot (calcul urinaire)",
  [TUBE_TYPE.POT_EXPECTORATION]: "Pot (expectoration)",
  [TUBE_TYPE.POT_SELLES]: "Pot (selles)",
  [TUBE_TYPE.POT_STERILE]: "Pot stérile",
  [TUBE_TYPE.POT_TUBE_LCR]: "Pot +/- tube (LCR)",
  [TUBE_TYPE.POT_TUBE_LIQUIDE]: "Pot +/- tube (liquide)",
  [TUBE_TYPE.POT_URINE_TUBE_BEIGE]: "Pot + tube beige (urines)",
  [TUBE_TYPE.POT_URINE_TUBE_VERT]: "Pot + tube vert (urines)",
  [TUBE_TYPE.POT_URINES]: "Pot (urines)",
  [TUBE_TYPE.POT_URINES_ADDIS]: "Pot (urines Addis)",
  [TUBE_TYPE.POT_URINES_TUBE]: "Pot +/- tubes (urines)",
  [TUBE_TYPE.SANG_TOTAL]: "Sang total",
  [TUBE_TYPE.SARSTEDT]: "Tubes Sarstedt",
  [TUBE_TYPE.SCOTCH]: "Scotch test",
  [TUBE_TYPE.SEC_GEL]: "Sec",
  [TUBE_TYPE.SEC_SANS_GEL]: "Sec (sans gel)",
  [TUBE_TYPE.SELLES]: "Selles",
  [TUBE_TYPE.SERINGUE]: "Seringue",
  [TUBE_TYPE.SERINGUE_HEPARINEE]: "Seringue héparinée",
  [TUBE_TYPE.SERUM]: "Sérum",
  [TUBE_TYPE.SOUCHE]: "Souche",
  [TUBE_TYPE.STRECK]: "STRECK",
  [TUBE_TYPE.TISSU_FFPE]: "Tissu FFPE",
  [TUBE_TYPE.TUBE_AIR_EXPIRE]: "Tubes (air expiré)",
  [TUBE_TYPE.TUBE_SANG_SELLES]: "Tubes sang dans les selles",
  [TUBE_TYPE.URINE]: "Urines",
  [TUBE_TYPE.URINE_UAP]: "Urine UAP",
  [TUBE_TYPE.URINES_5H]: "Urines 5h",
};

const NO_CAP_COLOR = { bg: "bg-slate-50", text: "text-slate-600", dot: "bg-slate-300" };

const TUBE_TYPE_COLORS = {
  [TUBE_TYPE.ACD_B]: NO_CAP_COLOR,
  [TUBE_TYPE.BOITE_PETRI]: NO_CAP_COLOR,
  [TUBE_TYPE.CITRATE]: { bg: "bg-blue-100", text: "text-blue-700", dot: "bg-blue-500" },
  [TUBE_TYPE.CYTOBROSSE_ANALE]: NO_CAP_COLOR,
  [TUBE_TYPE.CYTOBROSSE_COL_UTERUS]: NO_CAP_COLOR,
  [TUBE_TYPE.ECOUVILLON_FLOQSWAB]: NO_CAP_COLOR,
  [TUBE_TYPE.ECOUVILLON_TUBE]: NO_CAP_COLOR,
  [TUBE_TYPE.EDTA]: { bg: "bg-violet-100", text: "text-violet-700", dot: "bg-violet-500" },
  [TUBE_TYPE.EDTA_APROTININE]: { bg: "bg-pink-100", text: "text-pink-700", dot: "bg-pink-500" },
  [TUBE_TYPE.ESWAB]: NO_CAP_COLOR,
  [TUBE_TYPE.FECALSWAB]: NO_CAP_COLOR,
  [TUBE_TYPE.FLACON_AEROBIE]: NO_CAP_COLOR,
  [TUBE_TYPE.FLACON_URINE_12H]: NO_CAP_COLOR,
  [TUBE_TYPE.FLACON_URINE_24H]: NO_CAP_COLOR,
  [TUBE_TYPE.FLUORURE]: { bg: "bg-gray-100", text: "text-gray-700", dot: "bg-gray-500" },
  [TUBE_TYPE.HEPARINE_AVEC_GEL]: { bg: "bg-green-100", text: "text-green-700", dot: "bg-green-500" },
  [TUBE_TYPE.HEPARINE_SANS_GEL]: { bg: "bg-green-100", text: "text-green-700", dot: "bg-green-500" },
  [TUBE_TYPE.M4RT]: NO_CAP_COLOR,
  [TUBE_TYPE.NEUTRE_TRANSPARENT]: NO_CAP_COLOR,
  [TUBE_TYPE.PLASMA_CITR]: NO_CAP_COLOR,
  [TUBE_TYPE.POT]: NO_CAP_COLOR,
  [TUBE_TYPE.POT_BIOPSIE]: NO_CAP_COLOR,
  [TUBE_TYPE.POT_CALCUL_URINAIRE]: NO_CAP_COLOR,
  [TUBE_TYPE.POT_EXPECTORATION]: NO_CAP_COLOR,
  [TUBE_TYPE.POT_SELLES]: NO_CAP_COLOR,
  [TUBE_TYPE.POT_STERILE]: NO_CAP_COLOR,
  [TUBE_TYPE.POT_TUBE_LCR]: NO_CAP_COLOR,
  [TUBE_TYPE.POT_TUBE_LIQUIDE]: NO_CAP_COLOR,
  [TUBE_TYPE.POT_URINE_TUBE_BEIGE]: NO_CAP_COLOR,
  [TUBE_TYPE.POT_URINE_TUBE_VERT]: NO_CAP_COLOR,
  [TUBE_TYPE.POT_URINES]: NO_CAP_COLOR,
  [TUBE_TYPE.POT_URINES_ADDIS]: NO_CAP_COLOR,
  [TUBE_TYPE.POT_URINES_TUBE]: NO_CAP_COLOR,
  [TUBE_TYPE.SANG_TOTAL]: NO_CAP_COLOR,
  [TUBE_TYPE.SARSTEDT]: NO_CAP_COLOR,
  [TUBE_TYPE.SCOTCH]: NO_CAP_COLOR,
  [TUBE_TYPE.SEC_GEL]: { bg: "bg-yellow-100", text: "text-yellow-700", dot: "bg-yellow-500" },
  [TUBE_TYPE.SEC_SANS_GEL]: { bg: "bg-red-100", text: "text-red-700", dot: "bg-red-500" },
  [TUBE_TYPE.SELLES]: NO_CAP_COLOR,
  [TUBE_TYPE.SERINGUE]: NO_CAP_COLOR,
  [TUBE_TYPE.SERINGUE_HEPARINEE]: NO_CAP_COLOR,
  [TUBE_TYPE.SERUM]: { bg: "bg-yellow-100", text: "text-yellow-700", dot: "bg-yellow-500" },
  [TUBE_TYPE.SOUCHE]: NO_CAP_COLOR,
  [TUBE_TYPE.STRECK]: NO_CAP_COLOR,
  [TUBE_TYPE.TISSU_FFPE]: NO_CAP_COLOR,
  [TUBE_TYPE.TUBE_AIR_EXPIRE]: NO_CAP_COLOR,
  [TUBE_TYPE.TUBE_SANG_SELLES]: NO_CAP_COLOR,
  [TUBE_TYPE.URINE]: NO_CAP_COLOR,
  [TUBE_TYPE.URINE_UAP]: NO_CAP_COLOR,
  [TUBE_TYPE.URINES_5H]: NO_CAP_COLOR,
};

const normalizeText = (text) => {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\/\+\- ]/gi, "")
    .replace(/\s+/g, " ");
};

function levenshteinDistance(a, b) {
  const matrix = [];

  let i;
  for (i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }

  let j;
  for (j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  for (i = 1; i <= b.length; i++) {
    for (j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(matrix[i - 1][j - 1] + 1, Math.min(matrix[i][j - 1] + 1, matrix[i - 1][j] + 1));
      }
    }
  }

  return matrix[b.length][a.length];
}

function similarityScore(word1, word2) {
  const distance = levenshteinDistance(word1, word2);
  const maxLen = Math.max(word1.length, word2.length);
  return (1 - distance / maxLen) * 100;
}

const parseHourToMinutes = (value) => {
  if (typeof value !== "string") return null;
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value.trim());
  if (!match) return null;
  return parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
};

const validateTriggerGroups = (triggerGroups) => {
  for (const group of triggerGroups) {
    if (!group?.triggers?.length) return "Chaque groupe doit contenir au moins une condition";
    for (const trigger of group.triggers) {
      if ((trigger.type === "CODE_SOME" || trigger.type === "CODE_EVERY") && !trigger?.values?.length) return "Veuillez ajouter au moins un code dans chaque condition";
      if ((trigger.type === "EXAM_TYPE_SOME" || trigger.type === "EXAM_TYPE_EVERY") && !trigger?.values?.length)
        return "Veuillez sélectionner au moins un type d'analyse dans chaque condition";
      if (trigger.type === "MUTUELLE_CODE" && !trigger?.values?.length) return "Veuillez ajouter au moins un code mutuelle dans chaque condition";
      if (trigger.type === "QUESTION" && (!trigger?.field || !trigger?.values?.length)) return "Veuillez compléter la question et ses réponses dans chaque condition";
      if (trigger.type === "INFO" && trigger.field === "SEXE" && !trigger?.values?.[0]) return "Veuillez sélectionner un sexe";
      if (trigger.type === "INFO" && trigger.field === "AGE") {
        const [min, max] = trigger.values || [];
        if (min === undefined || max === undefined || min === "" || max === "") return "Veuillez compléter l'âge minimum et maximum";
      }
      if (trigger.type === "INFO" && trigger.field === "INS" && !trigger?.values?.[0]) return "Veuillez sélectionner un statut INS";
      if (trigger.type === "INFO" && trigger.field === "MUTUELLE" && !trigger?.values?.[0]) return "Veuillez sélectionner un statut mutuelle";
      if (trigger.type === "INFO" && trigger.field === "PATIENT_STATUS" && !trigger?.values?.[0]) return "Veuillez sélectionner un statut patient";
      if (trigger.type === "INFO" && trigger.field === "PRESCRIPTION" && !trigger?.values?.[0]) return "Veuillez sélectionner si l'ordonnance doit être présente ou absente";
      if (trigger.type === "INFO" && trigger.field === "EXAM" && !trigger?.values?.[0]) return "Veuillez sélectionner si un examen doit être détecté ou non";
      if (trigger.type === "INFO" && trigger.field === "PRESCRIPTION_WARNING" && !trigger?.values?.length) return "Veuillez sélectionner au moins une alerte d'ordonnance";
      if (trigger.type === "INFO" && trigger.field === "DAY" && !trigger?.values?.length) return "Veuillez sélectionner au moins un jour";
      if (trigger.type === "INFO" && trigger.field === "HOUR") {
        const [startHour, endHour] = trigger.values || [];
        if (!startHour || !endHour) return "Veuillez compléter l'heure de début et de fin";

        const startMinutes = parseHourToMinutes(startHour);
        const endMinutes = parseHourToMinutes(endHour);
        if (startMinutes === null || endMinutes === null) return "Veuillez utiliser le format d'heure HH:mm";
        if (startMinutes > endMinutes) return "L'heure de début doit être inférieure ou égale à l'heure de fin";
      }
      if (trigger.type === "FLAG" && !trigger?.values?.[0]) return "Veuillez sélectionner une alerte";
      if (trigger.type === "LABORATORY" && !trigger?.values?.length) return "Veuillez sélectionner au moins un laboratoire";
      if (trigger.type === "DELTA") {
        if (!["HOUR", "DATE", "DATETIME"].includes(trigger.field)) return "Veuillez sélectionner un type de delta (Heure, Date ou Date et heure)";

        if (trigger.field === "DATETIME") {
          const [dateSourceA, hourSourceA, dateSourceB, hourSourceB, operator, thresholdStr, unit] = trigger.values || [];
          const validateSource = (src, label) => {
            if (!src || (src !== "INFO" && !src.startsWith("QUESTION:"))) return `Veuillez configurer la source ${label} du delta`;
            if (src.startsWith("QUESTION:") && src.length <= 9) return `Veuillez sélectionner une question pour la source ${label}`;
            return null;
          };
          const srcError =
            validateSource(dateSourceA, "date A") || validateSource(hourSourceA, "heure A") || validateSource(dateSourceB, "date B") || validateSource(hourSourceB, "heure B");
          if (srcError) return srcError;
          if (![">=", "<="].includes(operator)) return "Veuillez sélectionner un opérateur (Minimum ou Maximum)";
          const threshold = parseFloat(thresholdStr);
          if (isNaN(threshold) || threshold < 0) return "Veuillez saisir un seuil positif pour le delta";
          if (!["MINUTES", "HOURS", "DAYS"].includes(unit)) return "Veuillez sélectionner une unité (Minutes, Heures ou Jours)";
        } else {
          const [sourceA, sourceB, operator, thresholdStr] = trigger.values || [];
          if (!sourceA || (sourceA !== "INFO" && !sourceA.startsWith("QUESTION:"))) return "Veuillez configurer la source A du delta";
          if (!sourceB || (sourceB !== "INFO" && !sourceB.startsWith("QUESTION:"))) return "Veuillez configurer la source B du delta";
          if (sourceA.startsWith("QUESTION:") && sourceA.length <= 9) return "Veuillez sélectionner une question pour la source A";
          if (sourceB.startsWith("QUESTION:") && sourceB.length <= 9) return "Veuillez sélectionner une question pour la source B";
          if (![">=", "<="].includes(operator)) return "Veuillez sélectionner un opérateur (Minimum ou Maximum)";
          const threshold = parseFloat(thresholdStr);
          if (isNaN(threshold) || threshold < 0) return "Veuillez saisir un seuil positif pour le delta";
        }
      }
    }
  }

  return null;
};

const summarizeGuidelineTriggers = (guideline) => {
  if (!guideline) return "—";
  const groups = Array.isArray(guideline.triggerGroups) ? guideline.triggerGroups : [];
  if (groups.length === 0) return "—";

  const codeSet = new Set();
  const examTypeSet = new Set();
  const mutuelleCodeSet = new Set();
  const flagSet = new Set();
  const sexes = new Set();
  const ageLabels = new Set();
  const days = new Set();
  const hourLabels = new Set();
  const questionIds = new Set();
  const deltaLabels = new Set();
  const patientStatusLabels = new Set();
  const negatedTypes = new Set();

  groups.forEach((group) => {
    (group?.triggers || []).forEach((trigger) => {
      if (!trigger) return;
      if (trigger.negate === true) negatedTypes.add(trigger.type === "INFO" ? `INFO_${trigger.field}` : trigger.type);
      if (trigger.type === "DELTA") {
        if (trigger.field === "DATETIME") {
          const operator = trigger.values?.[4] || ">=";
          const threshold = trigger.values?.[5] || "?";
          const unitMap = { MINUTES: "min", HOURS: "h", DAYS: "j" };
          const unit = unitMap[trigger.values?.[6]] || "min";
          deltaLabels.add(`${trigger.field}: ${operator} ${threshold}${unit}`);
        } else {
          const operator = trigger.values?.[2] || ">=";
          const threshold = trigger.values?.[3] || "?";
          const unit = trigger.field === "DATE" ? "j" : "min";
          deltaLabels.add(`${trigger.field}: ${operator} ${threshold}${unit}`);
        }
      }
      if (trigger.type === "CODE_SOME" || trigger.type === "CODE_EVERY") {
        (trigger.values || []).forEach((code) => {
          if (code) codeSet.add(code);
        });
      }
      if (trigger.type === "EXAM_TYPE_SOME" || trigger.type === "EXAM_TYPE_EVERY") {
        (trigger.values || []).forEach((type) => {
          if (type) examTypeSet.add(type);
        });
      }
      if (trigger.type === "MUTUELLE_CODE") {
        (trigger.values || []).forEach((code) => {
          if (code) mutuelleCodeSet.add(code);
        });
      }
      if (trigger.type === "FLAG") {
        (trigger.values || []).forEach((flag) => {
          if (flag) flagSet.add(flag);
        });
      }
      if (trigger.type === "QUESTION" && trigger.field) questionIds.add(trigger.field);
      if (trigger.type === "INFO") {
        if (trigger.field === "SEXE") {
          (trigger.values || []).forEach((value) => {
            if (value) sexes.add(value);
          });
        }
        if (trigger.field === "AGE") {
          const min = trigger.values?.[0];
          const max = trigger.values?.[1];
          if (min !== undefined || max !== undefined) {
            ageLabels.add(`${min !== undefined && min !== "" ? min : "?"}–${max !== undefined && max !== "" ? max : "?"}`);
          }
        }
        if (trigger.field === "DAY") {
          (trigger.values || []).forEach((value) => {
            if (value) days.add(value);
          });
        }
        if (trigger.field === "PATIENT_STATUS") {
          (trigger.values || []).forEach((value) => {
            if (value) patientStatusLabels.add(value === "KNOWN" ? "connu" : "nouveau");
          });
        }
        if (trigger.field === "HOUR") {
          const startHour = trigger.values?.[0];
          const endHour = trigger.values?.[1];
          if (startHour !== undefined || endHour !== undefined) {
            hourLabels.add(`${startHour !== undefined && startHour !== "" ? startHour : "?"}–${endHour !== undefined && endHour !== "" ? endHour : "?"}`);
          }
        }
      }
    });
  });

  const negatePrefix = (key) => (negatedTypes.has(key) ? "NON " : "");

  const segments = [];
  if (codeSet.size > 0) segments.push(`Codes: ${Array.from(codeSet).join(", ")}`);
  if (examTypeSet.size > 0)
    segments.push(
      `Types: ${Array.from(examTypeSet)
        .map((type) => EXAM_TYPE_LABEL[type] || type)
        .join(", ")}`,
    );
  if (mutuelleCodeSet.size > 0) segments.push(`${negatePrefix("MUTUELLE_CODE")}Mutuelles: ${Array.from(mutuelleCodeSet).join(", ")}`);
  if (flagSet.size > 0) segments.push(`${negatePrefix("FLAG")}Alertes: ${Array.from(flagSet).join(", ")}`);
  const infoSegments = [];
  if (sexes.size > 0)
    infoSegments.push(
      `${negatePrefix("INFO_SEXE")}Sexe: ${Array.from(sexes)
        .map((sexe) => SEXE_LABEL[sexe] || sexe)
        .join(" / ")}`,
    );
  if (ageLabels.size > 0) infoSegments.push(`${negatePrefix("INFO_AGE")}Âge: ${Array.from(ageLabels).join(", ")}`);
  if (days.size > 0)
    infoSegments.push(
      `${negatePrefix("INFO_DAY")}Jour: ${Array.from(days)
        .map((day) => DAY_LABEL[day] || day)
        .join(" / ")}`,
    );
  if (patientStatusLabels.size > 0) infoSegments.push(`${negatePrefix("INFO_PATIENT_STATUS")}Patient: ${Array.from(patientStatusLabels).join(" / ")}`);
  if (hourLabels.size > 0) infoSegments.push(`${negatePrefix("INFO_HOUR")}Heure: ${Array.from(hourLabels).join(", ")}`);
  if (infoSegments.length > 0) segments.push(`Infos: ${infoSegments.join(" • ")}`);
  if (questionIds.size > 0) segments.push(`${negatePrefix("QUESTION")}Questions: ${questionIds.size}`);
  if (deltaLabels.size > 0) segments.push(`${negatePrefix("DELTA")}Delta: ${Array.from(deltaLabels).join(", ")}`);

  return segments.length > 0 ? segments.join(" | ") : "—";
};

const TRIGGER_TYPE_LABELS = {
  CODE_SOME: "Codes analyses (au moins un)",
  CODE_EVERY: "Codes analyses (tous)",
  EXAM_TYPE_SOME: "Types d'analyse (au moins un)",
  EXAM_TYPE_EVERY: "Types d'analyse (tous)",
  INFO_SEXE: "Sexe",
  INFO_AGE: "Age",
  INFO_PATIENT_STATUS: "Statut patient",
  INFO_INS: "Statut INS",
  INFO_MUTUELLE: "Statut mutuelle",
  INFO_TYPE: "Type d'admission",
  MUTUELLE_CODE: "Codes mutuelle",
  LABORATORY: "Laboratoire",
  FLAG: "Alerte",
  INFO_DAY: "Jour de soumission",
  INFO_HOUR: "Heure de soumission",
  QUESTION: "Question",
  DELTA: "Delta temporel",
  ALWAYS: "Dans tous les cas",
};

const getItemTriggersMap = (item) => {
  const groups = Array.isArray(item.triggerGroups) ? item.triggerGroups : [];
  const result = {};
  for (const group of groups) {
    for (const trigger of group?.triggers || []) {
      if (!trigger?.type) continue;
      const key = trigger.type === "INFO" ? `INFO_${trigger.field}` : trigger.type;
      if (!TRIGGER_TYPE_LABELS[key]) continue;
      if (!result[key]) result[key] = new Set();

      if (
        trigger.type === "CODE_SOME" ||
        trigger.type === "CODE_EVERY" ||
        trigger.type === "EXAM_TYPE_SOME" ||
        trigger.type === "EXAM_TYPE_EVERY" ||
        trigger.type === "MUTUELLE_CODE" ||
        trigger.type === "FLAG"
      ) {
        for (const v of trigger.values || []) {
          if (v) result[key].add(v);
        }
      } else if (trigger.type === "INFO" && ["SEXE", "PATIENT_STATUS", "TYPE", "DAY", "INS", "MUTUELLE"].includes(trigger.field)) {
        for (const v of trigger.values || []) {
          if (v) result[key].add(v);
        }
      } else if (trigger.type === "INFO" && trigger.field === "AGE") {
        const min = trigger.values?.[0] || "0";
        const max = trigger.values?.[1] || "∞";
        result[key].add(`${min}-${max}`);
      } else if (trigger.type === "INFO" && trigger.field === "HOUR") {
        const min = trigger.values?.[0] || "0";
        const max = trigger.values?.[1] || "24";
        result[key].add(`${min}h-${max}h`);
      } else {
        result[key].add("Oui");
      }
    }
  }
  for (const k of Object.keys(result)) result[k] = [...result[k]];
  return result;
};

const buildTriggerFilterFields = (items, operators) => {
  const allTypes = {};
  for (const item of items) {
    const map = getItemTriggersMap(item);
    for (const [key, values] of Object.entries(map)) {
      if (!allTypes[key]) allTypes[key] = new Set();
      for (const v of values) allTypes[key].add(v);
    }
  }
  return Object.entries(allTypes)
    .sort(([a], [b]) => (TRIGGER_TYPE_LABELS[a] || "").localeCompare(TRIGGER_TYPE_LABELS[b] || ""))
    .map(([triggerKey, valuesSet]) => ({
      key: `trigger_${triggerKey}`,
      label: TRIGGER_TYPE_LABELS[triggerKey],
      operators,
      values: [...valuesSet].sort(),
      resolve: (item) => getItemTriggersMap(item)[triggerKey] || [],
    }));
};

const listStates = {};

const readListState = (key) => listStates[key] || null;

const writeListState = (key, state) => {
  listStates[key] = { ...listStates[key], ...state };
};

const clearListStates = () => {
  for (const key of Object.keys(listStates)) delete listStates[key];
};

const hasPendingSync = (item) => item._syncs?.some((s) => s.reviewStatus === REVIEW_STATUS.PENDING);

const guidelineText = (guideline) => guideline.secretaryGuideline || guideline.patientGuideline || guideline.ideGuideline || "";

const needsVerification = (item) => item.configStatus === CONFIG_STATUS.CREATED || item.configStatus === CONFIG_STATUS.UPDATED;

const isShowingDeleted = (filters) => (filters || []).some((filter) => filter.field === "deleted" && filter.values.includes("Oui"));

const isValidIp = (ip) => {
  const ipv4Regex = /^((25[0-5]|(2[0-4]|1\d|[1-9]|)\d)\.?\b){4}$/;
  const ipv6Regex =
    /^(([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9]))$/;
  return ipv4Regex.test(ip) || ipv6Regex.test(ip);
};

const PRESCRIPTION_FLAG_LABELS = { MISSING_EXAM: "Examen non-souligné", TRADUCTION_ERROR: "Erreur de traduction", OK: "OK" };

const PRESCRIPTION_WARNING_LABELS = {
  isHandwritten: "Texte manuscrit",
  hasExamWithoutCode: "Examen sans code",
  hasAdditionalInfo: "Infos complémentaires",
  hasRenewal: "Renouvellement",
  hasSchedule: "Date de réalisation",
  hasConditionNotTakenIntoAccount: "Conditions non prises en compte",
  hasCrossedOut: "Textes barrés",
};

const PRESCRIPTION_DOCTOR_STATUS_LABELS = {
  FOUND: "Inséré",
  NOT_FOUND: "Non trouvé",
  TOO_MANY_RESULTS: "Plusieurs résultats",
  NO_DOCTOR: "Aucun médecin détecté",
  DELETED: "Supprimé",
  ERROR: "Erreur",
};

const INPUT_CLASS_NAME =
  "flex-1 p-1.5 border border-gray-300 rounded-md text-sm text-gray-900 outline-none transition-all " +
  "placeholder:text-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500";

const EFFECT_TYPES = [
  { value: "ADD_CODES", label: "Ajouter des codes", hasValues: true },
  { value: "REMOVE_CODES", label: "Supprimer des codes", hasValues: true },
  { value: "REPLACE_CODES", label: "Remplacer des codes", hasValues: true },
  { value: "REMOVE_ALL_CODES_EXCEPT", label: "Supprimer tous les codes sauf", hasValues: true },
  { value: "REMOVE_CODES_OF_TYPE", label: "Supprimer toutes les analyses du type", hasValues: true },
  { value: "REMOVE_ALL_CODES_EXCEPT_OF_TYPE", label: "Supprimer toutes les analyses sauf du type", hasValues: true },
  { value: "DUPLICATE_ADMISSION", label: "Dupliquer le dossier", hasValues: false },
  { value: "ANSWER_QUESTION", label: "Répondre à une question", hasValues: false },
];

const EFFECT_TYPE_LABELS = {
  ADD_CODES: "Ajouter codes",
  REMOVE_CODES: "Supprimer codes",
  REPLACE_CODES: "Remplacer codes",
  REMOVE_ALL_CODES_EXCEPT: "Supprimer sauf",
  REMOVE_CODES_OF_TYPE: "Supprimer type",
  REMOVE_ALL_CODES_EXCEPT_OF_TYPE: "Conserver type",
  DUPLICATE_ADMISSION: "Dupliquer dossier",
  ANSWER_QUESTION: "Répondre question",
};

const EFFECT_TYPE_STYLES = {
  ADD_CODES: "bg-green-50 text-green-700",
  REMOVE_CODES: "bg-red-50 text-red-700",
  REPLACE_CODES: "bg-amber-50 text-amber-700",
  REMOVE_ALL_CODES_EXCEPT: "bg-orange-50 text-orange-700",
  REMOVE_CODES_OF_TYPE: "bg-rose-50 text-rose-700",
  REMOVE_ALL_CODES_EXCEPT_OF_TYPE: "bg-fuchsia-50 text-fuchsia-700",
  DUPLICATE_ADMISSION: "bg-blue-50 text-blue-700",
  ANSWER_QUESTION: "bg-purple-50 text-purple-700",
};

const formatRelative = (date) => {
  if (!date) return "";
  const diffMs = Date.now() - new Date(date).getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  if (diffMinutes < 1) return "à l'instant";
  if (diffMinutes < 60) return `${diffMinutes} min`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `il y a ${diffHours}h`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return "hier";
  if (diffDays < 7) return `il y a ${diffDays}j`;
  return new Date(date).toLocaleDateString("fr-FR");
};

const EFFECT_TYPE = {
  ADD_CODES: "ADD_CODES",
  REMOVE_CODES: "REMOVE_CODES",
  REPLACE_CODES: "REPLACE_CODES",
  REMOVE_ALL_CODES_EXCEPT: "REMOVE_ALL_CODES_EXCEPT",
  REMOVE_CODES_OF_TYPE: "REMOVE_CODES_OF_TYPE",
  REMOVE_ALL_CODES_EXCEPT_OF_TYPE: "REMOVE_ALL_CODES_EXCEPT_OF_TYPE",
  ANSWER_QUESTION: "ANSWER_QUESTION",
  DUPLICATE_ADMISSION: "DUPLICATE_ADMISSION",
  ADD_QUESTION_RECORDS: "ADD_QUESTION_RECORDS",
  REMOVE_QUESTION_RECORDS: "REMOVE_QUESTION_RECORDS",
  ADD_GUIDELINES: "ADD_GUIDELINES",
  REMOVE_GUIDELINES: "REMOVE_GUIDELINES",
  PRESCRIPTION_MODIFIED: "PRESCRIPTION_MODIFIED",
  INCOMPATIBLE_CODES_REMOVED: "INCOMPATIBLE_CODES_REMOVED",
};

const NON_COMMUNIQUE = "NON_COMMUNIQUE";

const IS_BETA_HOST = window.location.hostname === "beta.admin.querco.co";

const newObjectId = () => {
  const timestamp = Math.floor(Date.now() / 1000)
    .toString(16)
    .padStart(8, "0");
  const random = Array.from({ length: 16 }, () => Math.floor(Math.random() * 16).toString(16)).join("");
  return timestamp + random;
};

const getPrescriptionWarningLabels = (prescription) =>
  [
    (prescription.warning?.hasAdditionalInfo || prescription.warning?.hasRenewal || prescription.warning?.hasSchedule) && "Infos complémentaires",
    prescription.warning?.isHandwritten && "Textes manuscrits",
    (prescription.warning?.hasExamWithoutCode || prescription.acts?.some((act) => act.notInserted)) && "Analyses manquantes",
    prescription.warning?.hasConditionNotTakenIntoAccount && "Conditions (si, +/-)",
    prescription.warning?.hasCrossedOut && "Textes barrés",
  ].filter(Boolean);

const AUDIT_COLLECTION_LABELS = {
  admissions: "Dossiers",
  prescriptions: "Ordonnances",
  questionrecords: "Questions de dossier",
  questions: "Questions",
  guidelines: "Recommandations",
  code2: "Codes",
  exam2: "Examens",
  table2: "Tables",
  actions: "Actions",
  laboratories: "Laboratoires",
  clinics: "Cliniques",
  networks: "Réseaux",
  users: "Utilisateurs",
  clinicians: "IDE",
  patients: "Patients",
  settings: "Paramètres",
  syncoverrides: "Personnalisations de synchronisation",
  syncplans: "Plans de synchronisation",
  doctors: "Médecins",
  mutuelles: "Mutuelles",
  correspondants: "Correspondants",
  configTransfer: "Configuration",
  hiddencodes: "Codes masqués",
  ignoredterms: "Termes ignorés",
};

const AUDIT_ACTOR_KIND_LABELS = {
  user: "Secrétariat",
  clinician: "IDE",
  patient: "Patient",
  apikey: "Intégration",
  system: "Système",
};

const formatActorName = (actor) => (actor.kind === "user" ? actor.label.split("@")[0] : actor.label);

const auditActorKey = (actor) => {
  if (actor.kind === "patient") return "kind:patient";
  if (actor.kind === "system" || actor.kind === "apikey") return "kind:system,apikey";
  return `id:${actor.id}`;
};

const auditActorFilterParams = (value) => {
  if (value.startsWith("id:")) return { actorId: value.slice(3) };
  if (value.startsWith("kind:")) return { actorKind: value.slice(5) };
  return {};
};

const auditDistinctActors = (events) => {
  const counts = new Map();
  for (const event of events) {
    const key = auditActorKey(event.actor);
    counts.set(key, { actor: event.actor, count: (counts.get(key)?.count || 0) + 1 });
  }
  return [...counts.values()].sort((a, b) => b.count - a.count).map((entry) => entry.actor);
};

const PASSWORD_MIN_LENGTH = 10;
const hasPasswordLength = (password) => password.length >= PASSWORD_MIN_LENGTH;
const hasPasswordVariety = (password) => [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((pattern) => pattern.test(password)).length >= 3;
const isPasswordStrong = (password) => hasPasswordLength(password) && hasPasswordVariety(password);

const formatDayMonthYear = (isoDate) => {
  if (!isoDate) return "";
  const [year, month, day] = isoDate.split("-");
  if (!year || !month || !day) return isoDate;
  return `${day}/${month}/${year}`;
};

const normalizeDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().split("T")[0];
};

const sameFieldValue = (a, b) =>
  String(a ?? "")
    .trim()
    .toLowerCase() ===
  String(b ?? "")
    .trim()
    .toLowerCase();

const SEXE_LABELS = { M: "Homme", F: "Femme" };

const COMPARED_FIELDS = [
  { key: "firstName", activeKey: "patient.firstName", read: (p) => p?.firstName, changes: (p) => [{ field: "patient.firstName", value: p?.firstName || "" }] },
  { key: "lastName", activeKey: "patient.lastName", read: (p) => p?.lastName, changes: (p) => [{ field: "patient.lastName", value: p?.lastName || "" }] },
  { key: "birthLastName", activeKey: "patient.birthLastName", read: (p) => p?.birthLastName, changes: (p) => [{ field: "patient.birthLastName", value: p?.birthLastName || "" }] },
  { key: "sexe", activeKey: "patient.sexe", read: (p) => SEXE_LABELS[p?.sexe] || p?.sexe, changes: (p) => [{ field: "patient.sexe", value: p?.sexe || "" }] },
  {
    key: "birthdate",
    activeKey: "patient.birthdate",
    read: (p) => formatDayMonthYear(normalizeDate(p?.birthdate)),
    changes: (p) => [{ field: "patient.birthdate", value: p?.birthdate || "" }],
  },
  {
    key: "birthplace",
    activeKey: "patient.birthplaceCode",
    read: (p) => p?.birthplace,
    changes: (p) => [
      { field: "patient.birthplace", value: p?.birthplace || "" },
      { field: "patient.birthplaceCode", value: p?.birthplaceCode || "" },
    ],
  },
  { key: "INS", activeKey: "patient.INS", read: (p) => p?.INS?.value, changes: (p) => [{ field: "patient.INS.value", value: p?.INS?.value || "" }] },
  { key: "IPP", activeKey: "patient.IPP", read: (p) => p?.IPP, changes: (p) => [{ field: "patient.IPP", value: p?.IPP || "" }] },
  { key: "admissionId", activeKey: "patient.admissionId", read: (p) => p?.admissionId, changes: (p) => [{ field: "patient.admissionId", value: p?.admissionId || "" }] },
  { key: "hostService", activeKey: "patient.hostService", read: (p) => p?.hostService, changes: (p) => [{ field: "patient.hostService", value: p?.hostService || "" }] },
  { key: "room", activeKey: "patient.room", read: (p) => p?.room, changes: (p) => [{ field: "patient.room", value: p?.room || "" }] },
  { key: "phone", activeKey: "patient.phone", read: (p) => p?.phone, changes: (p) => [{ field: "patient.phone", value: p?.phone || "" }] },
  { key: "email", activeKey: "patient.email", read: (p) => p?.email, changes: (p) => [{ field: "patient.email", value: p?.email || "" }] },
  {
    key: "address",
    activeKey: "patient.address",
    read: (p) => [p?.address, p?.zipCode, p?.city].filter(Boolean).join(", "),
    changes: (p) => [
      { field: "patient.address", value: p?.address || "" },
      { field: "patient.zipCode", value: p?.zipCode || "" },
      { field: "patient.city", value: p?.city || "" },
    ],
  },
  { key: "weight", activeKey: "patient.weight", read: (p) => p?.weight, changes: (p) => [{ field: "patient.weight", value: p?.weight || "" }] },
  { key: "height", activeKey: "patient.height", read: (p) => p?.height, changes: (p) => [{ field: "patient.height", value: p?.height || "" }] },
  {
    key: "mutuelle.code",
    activeKey: "mutuelle.code",
    read: (p) => [p?.mutuelle?.code, p?.mutuelle?.name].filter(Boolean).join(" — "),
    changes: (p) => [
      { field: "patient.mutuelle.code", value: p?.mutuelle?.code || "" },
      { field: "patient.mutuelle.name", value: p?.mutuelle?.name || "" },
    ],
  },
  {
    key: "mutuelle.adherentNumber",
    activeKey: "mutuelle.adherentNumber",
    read: (p) => p?.mutuelle?.adherentNumber,
    changes: (p) => [{ field: "patient.mutuelle.adherentNumber", value: p?.mutuelle?.adherentNumber || "" }],
  },
  {
    key: "mutuelle.rightsFrom",
    activeKey: "mutuelle.rightsFrom",
    read: (p) => formatDayMonthYear(normalizeDate(p?.mutuelle?.rightsFrom)),
    changes: (p) => [{ field: "patient.mutuelle.rightsFrom", value: p?.mutuelle?.rightsFrom || "" }],
  },
  {
    key: "mutuelle.rightsTo",
    activeKey: "mutuelle.rightsTo",
    read: (p) => formatDayMonthYear(normalizeDate(p?.mutuelle?.rightsTo)),
    changes: (p) => [{ field: "patient.mutuelle.rightsTo", value: p?.mutuelle?.rightsTo || "" }],
  },
];

const DB_LOCKED_KEYS = ["sexe", "birthdate", "firstName", "birthLastName", "birthplace"];

const formatComparedValue = (value) => (value === undefined || value === null ? "" : String(value).trim());

const conflictsWith = (patient, match, activeFields) =>
  COMPARED_FIELDS.map((def) => {
    const [group, key] = def.activeKey.split(".");
    return {
      ...def,
      fieldState: activeFields?.[group]?.[key],
      current: formatComparedValue(def.read(patient)),
      db: formatComparedValue(def.read(match)),
      isLocked: DB_LOCKED_KEYS.includes(def.key),
    };
  }).filter((f) => f.fieldState !== FIELD_STATE.DISABLE && f.db !== "" && !sameFieldValue(f.current, f.db));

const isActRemovedByAction = (act) => act.isDeleted !== true && !(act.codes || []).length && (act.codesPredicted || []).length > 0 && !act.incompatibleWith?.length;

export {
  isActRemovedByAction,
  conflictsWith,
  formatDayMonthYear,
  normalizeDate,
  sameFieldValue,
  PASSWORD_MIN_LENGTH,
  guidelineText,
  hasPasswordLength,
  hasPasswordVariety,
  isPasswordStrong,
  formatRelative,
  normalizeText,
  SEXE_LABEL,
  USER_ROLE_LABEL,
  DAY_LABEL,
  AUDIT_COLLECTION_LABELS,
  AUDIT_ACTOR_KIND_LABELS,
  formatActorName,
  auditActorKey,
  auditActorFilterParams,
  auditDistinctActors,
  similarityScore,
  EXAM_TYPE,
  EXAM_TYPE_COLOR_BG,
  EXAM_TYPE_COLOR_TEXT,
  EXAM_TYPE_COLOR_BORDER,
  EXAM_TYPE_LABEL,
  REVIEW_STATUS,
  CONFIG_STATUS,
  CONFIG_STATUS_LABEL,
  SYNC_SOURCE,
  SYNC_SOURCE_LABEL,
  summarizeGuidelineTriggers,
  validateTriggerGroups,
  FIELD_STATE,
  resolveActiveFields,
  isRequiredFieldState,
  ADMISSION_STATUS,
  TERMINAL_ADMISSION_STATUSES,
  ADMISSION_STATUS_REASON,
  STATUS_REASON_LABELS,
  STATUS_CONFIG,
  FORCE_STATUS_TARGETS,
  getAllowedForceStatusTargets,
  EMAIL_TYPE_LABELS,
  EMAIL_TYPE_COLORS,
  EMAIL_TYPE,
  TRIGGER_TYPE_LABELS,
  buildTriggerFilterFields,
  readListState,
  writeListState,
  clearListStates,
  hasPendingSync,
  needsVerification,
  isShowingDeleted,
  isValidIp,
  INPUT_CLASS_NAME,
  PRESCRIPTION_FLAG_LABELS,
  PRESCRIPTION_WARNING_LABELS,
  PRESCRIPTION_DOCTOR_STATUS_LABELS,
  TUBE_TYPE,
  TUBE_TYPE_LABELS,
  TUBE_TYPE_COLORS,
  EFFECT_TYPES,
  EFFECT_TYPE_LABELS,
  EFFECT_TYPE_STYLES,
  EFFECT_TYPE,
  NON_COMMUNIQUE,
  IS_BETA_HOST,
  newObjectId,
  getPrescriptionWarningLabels,
};
